import type {
  QualityBasis,
  QualityReport,
  QualityReportListResponse,
  QualityReportProducer,
  QualityTier,
} from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  acceptQualityStandings,
  ApiError,
  askForQualityReport,
  fetchQualityReport,
  fetchQualityReports,
} from "../api.js";
import { useCellar } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { actionButton } from "../components/controls.js";
import { OwnersOnly } from "../components/OwnersOnly.js";
import { StatusLine } from "../components/StatusLine.js";
import { SwitchedOff } from "../components/SwitchedOff.js";
import { AiNotice } from "../components/AiNotice.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";
import { useFeatures } from "../session.js";

/**
 * *Kvalitet* — every wine the cellar holds, judged from its producer and
 * cuvée, and a proposed standing for every producer behind them. Task `10.26`;
 * the decision is in `project-scope.md`, 2026-09-27.
 *
 * **Reached from *Producenter*, not from the row of sections**, which already
 * wraps onto three lines on a phone. It belongs there: half of what it
 * proposes is a producer's standing, which is what that page sets.
 *
 * **The history is the page.** The newest report opens by default and every
 * earlier one is a line above it, because the report is kept for exactly the
 * reason the drinking-window suggestions are: *it said this last spring* is
 * worth reading beside *it says this now*.
 *
 * **A producer's standing is proposed, never written.** Each proposal that
 * differs from what the producer stands at now has its own *Använd*, and there
 * is one for all of them. Proposals that already agree are counted rather than
 * listed — a list of a hundred producers where ninety say *already so* is a
 * list nobody reads to the ten that matter.
 */

const TIERS: readonly QualityTier[] = ["Top", "VeryGood", "Good", "Everyday"];
const BASES: readonly QualityBasis[] = ["Producer", "Cuvee", "Appellation"];

type History =
  | { status: "loading" }
  | { status: "ready"; list: QualityReportListResponse }
  | { status: "failed" };

type Shown =
  | { status: "none" }
  | { status: "loading"; id: string }
  | { status: "ready"; report: QualityReport }
  | { status: "failed"; id: string };

function when(iso: string): string {
  return new Date(iso).toLocaleString(locale(), {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(locale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Quality() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  // The cellar's owner and nobody else — the role itself rather than
  // `useMayWrite`, which admits the same person today and is a different
  // question: see `routes/quality.ts`. Null until the role is known.
  const cellar = useCellar();
  const isOwner = cellar === null ? null : cellar.role === "Owner";
  // The administrator's switch, since 2026-09-28 — the page with the question,
  // history and all, because the page is nothing but the report.
  const { quality } = useFeatures();

  const [history, setHistory] = useState<History>({ status: "loading" });
  const [shown, setShown] = useState<Shown>({ status: "none" });
  const [asking, setAsking] = useState<"idle" | "asking" | { failed: string }>("idle");
  const [accepting, setAccepting] = useState(false);
  const [acceptFailed, setAcceptFailed] = useState(false);

  const show = useCallback(
    (id: string) => {
      setShown({ status: "loading", id });
      void fetchQualityReport(cellarId, id)
        .then((report) => setShown({ status: "ready", report }))
        .catch(() => setShown({ status: "failed", id }));
    },
    [cellarId],
  );

  // Not fetched until the reader is known to be the owner: anybody else would
  // only be answered 403, and the page already knows what to say to them.
  const load = useCallback(() => {
    if (isOwner !== true || !quality) return;
    void fetchQualityReports(cellarId)
      .then((list) => setHistory({ status: "ready", list }))
      .catch(() => setHistory({ status: "failed" }));
  }, [cellarId, isOwner, quality]);

  useReload(load);

  // The newest report opens by itself the first time the history arrives, and
  // is left alone after that: somebody reading last spring's report should not
  // be moved off it because the list was fetched again.
  const newest = history.status === "ready" ? history.list.reports[0]?.id : undefined;
  useEffect(() => {
    if (newest !== undefined && shown.status === "none") show(newest);
  }, [newest, shown.status, show]);

  // The button wakes up on the hour by itself. A page left open over coffee
  // should not need reloading to find out that the hour has passed.
  const nextAllowedAt = history.status === "ready" ? history.list.nextAllowedAt : null;
  useEffect(() => {
    if (nextAllowedAt === null) return;
    const wait = Math.max(0, new Date(nextAllowedAt).getTime() - Date.now()) + 1000;
    const timer = setTimeout(load, wait);
    return () => clearTimeout(timer);
  }, [nextAllowedAt, load]);

  if (isOwner === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="quality" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  if (!quality) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="quality" />
        <SwitchedOff cellarId={cellarId} />
      </section>
    );
  }

  const ask = () => {
    setAsking("asking");
    void askForQualityReport(cellarId)
      .then((report) => {
        setAsking("idle");
        setShown({ status: "ready", report });
        load();
      })
      .catch((error: unknown) => {
        setAsking({ failed: error instanceof ApiError ? error.code : "unknown" });
        // A refusal inside the hour means the history on screen is out of date
        // about when — another tab asked, or this one was left open.
        load();
      });
  };

  const accept = (report: QualityReport, rows: QualityReportProducer[]) => {
    setAccepting(true);
    setAcceptFailed(false);
    void acceptQualityStandings(
      cellarId,
      report.id,
      rows.map((row) => row.id),
    )
      .then((updated) => {
        setShown({ status: "ready", report: updated });
        load();
      })
      .catch(() => setAcceptFailed(true))
      .finally(() => setAccepting(false));
  };

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="quality" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("quality.intro")}</p>

      <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={ask}
          disabled={
            asking === "asking" || nextAllowedAt !== null || history.status !== "ready"
          }
          className={actionButton}
        >
          {asking === "asking" ? t("quality.asking") : t("quality.ask")}
        </button>
        {nextAllowedAt !== null && asking !== "asking" && (
          <span className="text-muted text-sm">
            {t("quality.nextAt", { time: clock(nextAllowedAt) })}
          </span>
        )}
      </div>

      {asking === "asking" && <StatusLine>{t("quality.askingLong")}</StatusLine>}
      {typeof asking === "object" && (
        <p role="alert" className="text-past mt-4 max-w-prose text-sm">
          {t(
            asking.failed === "too_soon"
              ? "quality.tooSoon"
              : asking.failed === "not_configured"
                ? "quality.notConfigured"
                : asking.failed === "switched_off"
                  ? "features.switchedOff"
                  : asking.failed === "nothing_to_assess"
                    ? "quality.nothingToAssess"
                    : "quality.failed",
          )}
        </p>
      )}

      {history.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {history.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("quality.loadFailed")}
        </p>
      )}

      {/* Not while the first one is being made: *no report yet* under a line
          saying one is on its way is two sentences contradicting each other. */}
      {history.status === "ready" &&
        history.list.reports.length === 0 &&
        asking !== "asking" && (
          <p className="text-muted mt-8 max-w-prose text-sm">{t("quality.empty")}</p>
        )}

      {/* The history. One line each, the shown one marked rather than linked:
          it is a choice of which report to read, not a place to go. */}
      {history.status === "ready" && history.list.reports.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl">{t("quality.history")}</h2>
          <ul className="border-line mt-3 border-t">
            {history.list.reports.map((report) => {
              const current =
                (shown.status === "ready" && shown.report.id === report.id) ||
                ((shown.status === "loading" || shown.status === "failed") &&
                  shown.id === report.id);
              return (
                <li key={report.id} className="border-line border-b">
                  <button
                    type="button"
                    onClick={() => show(report.id)}
                    aria-current={current ? "true" : undefined}
                    className={`flex w-full flex-wrap items-baseline justify-between gap-x-4 py-3 text-left text-sm ${
                      current ? "text-accent" : "text-ink hover:text-accent"
                    }`}
                  >
                    <span>{when(report.askedAt)}</span>
                    <span className="text-muted">
                      {t("quality.wineCount", { count: report.wineCount })}
                      {report.acceptedCount > 0 &&
                        ` · ${t("quality.acceptedCount", { count: report.acceptedCount })}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {shown.status === "loading" && (
        <p className="text-muted mt-8 text-sm">{t("loading")}</p>
      )}
      {shown.status === "failed" && (
        <p role="alert" className="text-past mt-8 text-sm">
          {t("quality.reportFailed")}
        </p>
      )}
      {shown.status === "ready" && (
        <Report
          cellarId={cellarId}
          report={shown.report}
          accepting={accepting}
          acceptFailed={acceptFailed}
          onAccept={(rows) => accept(shown.report, rows)}
        />
      )}
    </section>
  );
}

function Report({
  cellarId,
  report,
  accepting,
  acceptFailed,
  onAccept,
}: {
  cellarId: string;
  report: QualityReport;
  accepting: boolean;
  acceptFailed: boolean;
  onAccept: (rows: QualityReportProducer[]) => void;
}) {
  const { t } = useTranslation();

  // A proposal is open when its producer still exists and does not already
  // stand where it proposes — whether because it was accepted, or because the
  // owner set the same standing by hand.
  const open = report.producers.filter(
    (row) => row.current !== null && row.current !== row.standing,
  );
  const agreeing = report.producers.length - open.length;

  return (
    <article className="mt-10">
      <h2 className="font-display text-2xl">{when(report.askedAt)}</h2>
      <p className="text-muted mt-1 text-xs">
        {t("quality.byline", {
          model: report.model,
          who: report.askedBy ?? t("quality.someone"),
        })}
      </p>
      <AiNotice className="mt-2" />
      {/* Where this comes from, said once per report and in so many words: the
          model's own knowledge on the day it was asked, and nothing looked up.
          Without it, *grund: producenten* below would read as a reference to
          something somebody could go and check. */}
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t("quality.source", { model: report.model })}
      </p>
      <p className="text-muted mt-1 max-w-prose text-sm">
        {BASES.map((basis) => ({
          basis,
          count: report.wines.filter((wine) => wine.basis === basis).length,
        }))
          .filter(({ count }) => count > 0)
          .map(({ basis, count }) => t(`quality.basisCount.${basis}`, { count }))
          .join(" · ")}
      </p>
      {/* The model does not always answer every wine it is asked about. Said
          plainly, because a report that quietly lost a wine reads as whole. */}
      {report.wines.length < report.winesAsked && (
        <p className="text-muted mt-2 max-w-prose text-sm">
          {t("quality.unanswered", { count: report.winesAsked - report.wines.length })}
        </p>
      )}

      {TIERS.map((tier) => {
        const wines = report.wines.filter((wine) => wine.tier === tier);
        if (wines.length === 0) return null;
        return (
          <section key={tier} className="mt-8">
            <h3 className="font-display text-lg">
              {t(`quality.tier.${tier}`)}{" "}
              <span className="text-muted text-sm">({wines.length})</span>
            </h3>
            <ul className="border-line mt-2 border-t">
              {wines.map((wine) => {
                const vintage = wine.isNonVintage
                  ? "NV"
                  : (wine.vintage?.toString() ?? "");
                const title = (
                  <>
                    <span className="text-muted tabular-nums">{wine.number}</span>{" "}
                    <span className="font-display">{wine.producer}</span> — {wine.name}
                    {vintage !== "" && ` ${vintage}`}
                  </>
                );
                return (
                  <li
                    key={`${String(wine.number)}-${wine.name}`}
                    className="border-line border-b py-3"
                  >
                    {wine.wineId === null ? (
                      <p>{title}</p>
                    ) : (
                      <Link
                        to={`/kallare/${cellarId}/viner/${wine.wineId}`}
                        className="hover:text-accent block"
                      >
                        {title}
                      </Link>
                    )}
                    <p className="text-muted mt-1 max-w-prose text-sm">
                      {wine.basis === "Appellation" && (
                        <span className="text-accent" title={t("quality.unsure")}>
                          {t("quality.unsureMark")}{" "}
                        </span>
                      )}
                      {wine.reason}
                    </p>
                    {/* What the tier rests on, after the sentence rather than
                        before it: the sentence is what is read, and this is
                        what somebody doubting it looks for next. */}
                    <p className="text-muted mt-1 text-xs">
                      {t("quality.basisLabel", {
                        basis: t(`quality.basis.${wine.basis}`),
                      })}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <section className="mt-10">
        <h3 className="font-display text-lg">{t("quality.producers")}</h3>
        <p className="text-muted mt-1 max-w-prose text-sm">
          {open.length === 0
            ? t("quality.allAgree")
            : t("quality.openCount", { count: open.length })}
          {agreeing > 0 && ` ${t("quality.agreeCount", { count: agreeing })}`}
        </p>

        {open.length > 1 && (
          <button
            type="button"
            onClick={() => onAccept(open)}
            disabled={accepting}
            className={`${actionButton} mt-4`}
          >
            {t("quality.acceptAll", { count: open.length })}
          </button>
        )}
        {acceptFailed && (
          <p role="alert" className="text-past mt-4 text-sm">
            {t("quality.acceptFailed")}
          </p>
        )}

        {open.length > 0 && (
          <ul className="border-line mt-4 border-t">
            {open.map((row) => (
              <li
                key={row.id}
                className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b py-3"
              >
                <div>
                  <span className="font-display">{row.name}</span>
                  <span className="text-muted ml-3 text-sm">
                    {t(`standing.${row.current ?? row.priorStanding}`)} →{" "}
                    <span className="text-ink">{t(`standing.${row.standing}`)}</span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onAccept([row])}
                  disabled={accepting}
                  aria-label={t("quality.acceptFor", { name: row.name })}
                  className="text-accent text-sm underline-offset-4 hover:underline disabled:opacity-40"
                >
                  {t("quality.accept")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-muted mt-8 max-w-prose text-xs">{t("quality.disclaimer")}</p>
    </article>
  );
}
